import { Controller } from "@hotwired/stimulus"
import { fitToViewport } from "../embed/modal"
import { wireSwipe } from "../embed/sheet"

// Review & sign on the guest's proposal page (Events v2 agreements, N1): one native dialog holding the agreement's
// details, its text, the typed name and the consent line, opened from the page's card and the sticky bar. On a phone
// it is a bottom sheet that drags shut from its top. Its body is the one scroller, between a fixed header and footer,
// so Accept and sign unlocks once the body has been scrolled to the end, however the guest got there; until then the
// text fades out at the foot. The server draws the lock (aria-disabled), so a page
// coming back from a refused sign that was already read opens unlocked. While locked, a tap or Enter reads on instead
// of posting. The browser reports read_to_end only once unlocked; the server records it and never refuses on it.
// Review and approve, for changes after booking (N2), is the same dialog with its own button words (signLabel).
export default class extends Controller {
  static targets = ["dialog", "title", "grab", "body", "button", "readToEnd", "name", "preview", "status"]
  static values = { signLabel: { type: String, default: "Accept and sign" } }

  connect() {
    this.locked = this.buttonTarget.getAttribute("aria-disabled") === "true"
    this.check = this.check.bind(this)
    // A tap on a link in the terms opens it beside the page, so the typed name stays.
    this.dialogTarget.querySelectorAll(".pe-sign-terms a[href]").forEach((link) => {
      link.target = "_blank"
      link.rel = "noopener"
    })
    // Back from the deposit page restores this page from the cache, dialog open, on a proposal now booked.
    this.onPageShow = (event) => { if (event.persisted) window.location.reload() }
    this.sent = false
    window.addEventListener("pageshow", this.onPageShow)
    if (this.dialogTarget.hasAttribute("data-reopen")) this.open()
  }

  // Its close event would come after the actions are gone, so the page is put back here.
  disconnect() {
    window.removeEventListener("pageshow", this.onPageShow)
    clearTimeout(this.unsend)
    if (!this.dialogTarget.open) return
    this.dialogTarget.close()
    this.closed()
  }

  open(event) {
    event?.preventDefault()
    const dialog = this.dialogTarget
    if (dialog.open) return
    this.opener = event?.currentTarget || null
    // showModal() focuses the first field with a scroll, which on a phone jumps the sheet: hold focus out of it, then
    // put it on the title without scrolling (embed/modal.js does the same).
    const [x, y] = [window.scrollX, window.scrollY]
    dialog.inert = true
    dialog.showModal()
    dialog.inert = false
    this.bodyTarget.scrollTop = 0
    if (window.scrollX !== x || window.scrollY !== y) window.scrollTo(x, y)
    this.titleTarget.focus({ preventScroll: true })
    this.pageOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"
    this.unfit = fitToViewport(dialog, dialog)
    this.unswipe = wireSwipe(this.grabTarget, dialog, () => this.close(), this.bodyTarget)
    window.visualViewport?.addEventListener("resize", this.check)
    this.check()
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  // The dialog's close event: the ✕, Esc and the drag all end here.
  closed() {
    window.visualViewport?.removeEventListener("resize", this.check)
    document.documentElement.style.overflow = this.pageOverflow || ""
    this.unfit?.()
    this.unswipe?.()
    this.unfit = this.unswipe = null
    this.opener?.focus()
  }

  // At the bottom, with the consent line fully above the footer, the fade goes and the button unlocks. A body not yet
  // laid out has no height, so it would read as scrolled to the end.
  check() {
    const body = this.bodyTarget
    if (!this.dialogTarget.open || body.clientHeight === 0) return
    const atEnd = body.scrollTop + body.clientHeight >= body.scrollHeight - 2
    body.classList.toggle("pe-sign-body--more", !atEnd)
    if (atEnd && this.locked) this.unlock()
  }

  unlock() {
    this.locked = false
    this.buttonTarget.removeAttribute("aria-disabled")
    this.buttonTarget.textContent = this.signLabelValue
    this.readToEndTarget.disabled = false
    this.statusTarget.textContent = "You can sign now."
  }

  // The form's submit: the first post goes, a second tap while it loads does not (each one would sign again and
  // come back refused). A post that never leaves the page (offline, a stopped load) frees the button after a while.
  submit(event) {
    if (this.sent) {
      event.preventDefault()
    } else if (event.submitter === this.buttonTarget) {
      this.sent = true
      clearTimeout(this.unsend)
      this.unsend = setTimeout(() => { this.sent = false }, 10000)
    }
  }

  // The button carries the session's token, so a locked tap must not post.
  sign(event) {
    if (!this.locked) return
    event.preventDefault()
    this.readOn()
  }

  // Enter in the name signs once unlocked, rather than going through the form's first button, the hidden "Update
  // total", which would reload the page.
  enter(event) {
    if (event.key !== "Enter" || event.isComposing) return
    event.preventDefault()
    if (this.locked) {
      this.readOn()
    } else if (this.buttonTarget.form.requestSubmit) {
      this.buttonTarget.form.requestSubmit(this.buttonTarget)
    } else {
      this.buttonTarget.click() // Safari before 16 has no requestSubmit
    }
  }

  readOn() {
    const body = this.bodyTarget
    body.scrollTop += Math.max(body.clientHeight - 120, 100)
  }

  previewName() {
    this.previewTarget.textContent = this.nameTarget.value.trim()
  }
}
